import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'

import { ReApi } from '@/composables/query'
import { Actions as AuthActions } from '@/store/Auth'
import store from '@/store/index.js'
import { Actions } from '@/store/Log'
import isReportPageOpened from '@/utils/reportPage'

// TODO: replace explicit typing by inferred typing from backend OpenAPI spec
interface ReApiErrorResponseData {
  errors?: unknown[] | Record<string, unknown>
  warnings?: unknown[]
  status?: string
  debug_info?: string
  proxy_error?: string
  [key: string]: unknown
}

interface ReAxiosError extends AxiosError<ReApiErrorResponseData> {
  handled_by_axios?: boolean
  config?: InternalAxiosRequestConfig & {
    manualErrorHandling?: boolean
    metadata?: {
      silentError?: boolean
    }
  }
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    manualErrorHandling?: boolean
    metadata?: {
      silentError?: boolean
    }
  }
}

// @ts-expect-error: Handle types
let routerInstance = null

// @ts-expect-error: Handle types
export function setRouter(router) {
  routerInstance = router
}

// create axios instance with base Url
let baseURL = import.meta.env.VITE_APP_API_URL
if (baseURL && baseURL.endsWith('/')) {
  // Remove any trailing slash
  baseURL = baseURL.slice(0, -1)
}

const instance = baseURL ? axios.create({ baseURL }) : axios.create()

// interceptors for request and response
instance.interceptors.request.use(axiosReConfigInterceptor)
instance.interceptors.response.use(axiosReResponseInterceptor, axiosReErrorHandler)

function axiosReConfigInterceptor(config: InternalAxiosRequestConfig) {
  // Do something before request is sent
  return config
}

function axiosReErrorHandler(err: ReAxiosError) {
  const isDev = import.meta.env.MODE === 'development'

  // Any status codes that falls outside the range of 2xx cause this function to trigger
  /* if (error.response.status !== 404) {
        // can update message key in store and use it in toast
        Toast.open({
            duration: 5000,
            message: `Something's not good, also I'm on <b>bottom</b>`,
            position: 'is-bottom-right',
            type: 'is-danger'
        })
        //store.commit('API_MESSAGE', error.response.data)
    } */

  // console.warn('Axios error', err.response)

  // Queue errors and warnings in store:

  // Explicitely disable error handling
  if (err.config?.manualErrorHandling)
    return Promise.reject(err)

  try {
    const debugContext = {
      axiosConfig: err.response?.config,
      axiosHeaders: err.response?.headers,
    }

    if (err.response) {
      // 401 authentication error handling
      // @ts-expect-error: Handle types
      if (err.response.status === 401 && routerInstance) {
        // Check if it's a user_not_authenticated error
        const isUserNotAuthenticated = Array.isArray(err.response.data?.errors)
          ? err.response.data.errors.some(
              // @ts-expect-error: Handle types
              error => error.error_code === 'user_not_authenticated',
            )
          : false

        if (isUserNotAuthenticated) {
          // Clear authentication state
          store.dispatch(AuthActions.LOGOUT)

          const currentRoute = routerInstance.currentRoute.value
          routerInstance.push({
            name: 'login',
            query: {
              msg: 'session-expired',
              r: `/app${currentRoute.fullPath}`,
            },
          })
          return Promise.reject(err)
        }
      }
      // Hack to avoid redirecting the user to a different page when a PATCH fails (usually other_data patches)
      const isPatch = err.config?.method === 'patch'
      if (!isDev && !isPatch) {
        // 403 redirection
        // @ts-expect-error: Handle types
        if (err.response.status === 403 && routerInstance) {
          routerInstance.push({
            name: 'forbidden',
            query: {
              details: JSON.stringify({
                url: err.response.config?.url,
                data: err.response.data,
              }),
            },
          })
          return Promise.reject(err)
        }

        if (
          err.response.status >= 500
          // Sporadic 502's are expected from the backend and should not be fatal
          && err.response.status !== 502
          && typeof err.response.data !== 'object'
        ) {
          const langRegex = /en-BE|fr-BE|nl-BE|de-BE/
          const langMatch = window.location.pathname.match(langRegex)
          // @ts-expect-error: Handle types
          const lang = langMatch[0] || 'en-BE'
          const invalidPath = `/app/${lang}/invalid`
          // vue router might not be available at this point (eg. error on frontend_settings)
          if (window.location.pathname !== invalidPath) {
            window.location.pathname = invalidPath
            return
          }
        }
      }

      if (err.response.data) {
        if (
          err.response.status === 422
          && err.response.data.errors
          && typeof err.response.data.errors === 'object'
          && !Array.isArray(err.response.data.errors)
        ) {
          // Marshmallow validation error
          const error = {
            error_code: 'validation_error',
            debug: {
              context: {
                raw_response: err.response.data.errors,
              },
            },
          }
          store.dispatch(Actions.LOG_ERROR, {
            error,
            errorType: 'error',
            status: err.response.status,
            debugContext,
          })
        }
        else if (err.response.data.errors || err.response.data.warnings) {
          // TODO: add specific field to response data in backend,
          //  to identify RE error schema?
          if (Array.isArray(err.response.data.errors)) {
            for (const error of err.response.data.errors) {
              if (err.response.config.metadata?.silentError && err.response.status < 500) {
                break
              }
              store.dispatch(Actions.LOG_ERROR, {
                error,
                errorType: 'error',
                status: err.response.status,
                debugContext,
              })
            }
          }
          if (Array.isArray(err.response.data.warnings)) {
            for (const error of err.response.data.warnings) {
              store.dispatch(Actions.LOG_ERROR, {
                error,
                errorType: 'warning',
                status: err.response.status,
                debugContext,
              })
            }
          }
          else {
            let error_code = err.response.data.status
              ? `server_error_status_${err.response.data.status.replace(' ', '_').toLowerCase()}`
              : `unknown_server_error_${err.response.status}`

            let debug_message = err.response.data.debug_info
              ? err.response.data.debug_info
              : 'Unknown server-side error: no debug info available'

            if (err.response.data.proxy_error && err.response.data.proxy_error.includes('ECONNREFUSED')) {
              error_code = `server_connection_refused`
              debug_message = 'Server connection failed'
            }
            const error = {
              error_code,
              debug: {
                message: debug_message,
                context: { raw_response: err.response.data },
              },
            }
            store.dispatch(Actions.LOG_ERROR, {
              error,
              errorType: 'error',
              status: err.response.status,
              debugContext,
            })
          }
        }
        else {
          let error_code = err.response.data.status
            ? `server_error_status_${err.response.data.status.replace(' ', '_').toLowerCase()}`
            : `unknown_server_error_${err.response.status}`

          let debug_message = err.response.data.debug_info
            ? err.response.data.debug_info
            : 'Unknown server-side error: no debug info available'

          if (err.response.data.proxy_error && err.response.data.proxy_error.includes('ECONNREFUSED')) {
            error_code = `server_connection_refused`
            debug_message = 'Server connection failed'
          }
          const error = {
            error_code,
            debug: {
              message: debug_message,
              context: { raw_response: err.response.data },
            },
          }
          store.dispatch(Actions.LOG_ERROR, {
            error,
            errorType: 'error',
            status: err.response.status,
            debugContext,
          })
        }
      }
      else {
        // No data: unknown error (most likely client-side)
        const error = {
          error_code: 'unknown_client_error',
          debug: {
            message: 'Request error with no response data: most likely a connection issue on the client side',
            context: err.response,
            status: err.response.status,
          },
        }
        store.dispatch(Actions.LOG_ERROR, {
          error,
          errorType: 'error',
          debugContext,
        })
      }
    }
    else {
      // No response: most likely code exception
      const error = {
        error_code: 'unknown_exception',
        debug: {
          message: 'Request error with no response: most likely a code exception',
          context: err,
        },
      }
      // Should we add an errorType 'exception'?
      store.dispatch(Actions.LOG_ERROR, { error, errorType: 'error', debugContext })
    }
  }
  catch (err2) {
    // Who's catching the catcher?
    if (isDev) {
      console.error('Error while handling request error:', err)
      throw err2
    }
    else {
      const error = {
        error_code: 'unknown_exception',
        debug: {
          message: `Exception while handling request error ${err}: most likely a code exception`,
          context: err2,
        },
      }
      store.dispatch(Actions.LOG_ERROR, { error, errorType: 'error' })
    }
  }

  err.handled_by_axios = true

  if (isReportPageOpened() && err.response && err.response.status >= 500) {
    // Creates a unhandled rejection to be caught by the global error handler,
    Promise.reject(err)
  }
  else {
    return Promise.reject(err)
  }
}

function axiosReResponseInterceptor(response: AxiosResponse) {
  // Any status code that lie within the range of 2xx cause this function to trigger

  // Do something with response data
  return response
}

/**
 * Creates a new axios instance that can have its baseURL updated dynamically,
 * inheriting all interceptors from the main instance.
 * @param api - The API enum value (ReApi.AUTH or ReApi.VALUATION)
 * @returns A new axios instance with interceptors copied and updateable baseURL
 */
export function createAxiosWithApiUrl(api?: ReApi) {
  const config = store.getters.getConfig
  const baseURL
    = api === ReApi.AUTH ? config.AUTH_API_URL : api === ReApi.VALUATION ? config.VALUATION_API_URL : null

  const newInstance = axios.create({ baseURL })

  // Set interceptors:
  newInstance.interceptors.request.use(axiosReConfigInterceptor)
  newInstance.interceptors.response.use(axiosReResponseInterceptor, axiosReErrorHandler)

  return newInstance
}

export { ReApi }

export default instance
